import { Component, OnInit } from '@angular/core';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { SessionHelper } from '@src/app/helpers/sessionHelper';
import { Task } from '@src/app/models/task';

import { UntilDestroy } from '@ngneat/until-destroy';

@UntilDestroy({ checkProperties: true })
@Component({
  selector: 'app-tasktrackerchart',
  templateUrl: './tasktrackerchart.component.html',
  styleUrls: ['./tasktrackerchart.component.css'],
})
export class TasktrackerchartComponent implements OnInit {
  private root!: am5.Root;
  public todayTasksCount: number = 0;
  public last14DaysData: { date: string; count: number }[] = [];

  ngOnInit(): void {
    this.processTaskData();
    this.createChart();
  }

  private processTaskData(): void {
    const taskList: Task[] = JSON.parse(SessionHelper.getPortalTaskList());
    const today = new Date().toISOString().split('T')[0];
    const last14Days = Array.from({ length: 14 }, (_, i) => {
      const date = new Date();
      date.setDate(date.getDate() - i);
      return date.toISOString().split('T')[0];
    }).reverse();

    // Initialize data for the last 14 days
    const dataMap: { [key: string]: number } = last14Days.reduce((acc, date) => {
      acc[date] = 0;
      return acc;
    }, {});

    // Count tasks per day
    taskList.forEach((task) => {
      if (task.taskstate === 'Done') {
        const taskDate = task.validfrom.substring(0, 10);
        if (dataMap[taskDate] !== undefined) {
          dataMap[taskDate] += 1;
        }
        if (taskDate === today) {
          this.todayTasksCount += 1;
        }
      }
    });

    // Format data for the chart
    this.last14DaysData = last14Days.map((date) => ({
      date,
      count: dataMap[date] || 0,
    }));
  }

  private createChart(): void {
    this.root = am5.Root.new('chartdiv');
    this.root.setThemes([am5themes_Animated.new(this.root)]);
  
    const chart = this.root.container.children.push(
      am5xy.XYChart.new(this.root, {
        panX: false,
        panY: false,
        wheelX: 'none',
        wheelY: 'none',
        layout: this.root.verticalLayout,
        maxTooltipDistance: 0,
      })
    );
  
    const cursor = chart.set(
      'cursor',
      am5xy.XYCursor.new(this.root, {
        behavior: 'zoomX',
      })
    );
    cursor.lineY.set('visible', false);
  
    const xAxis = chart.xAxes.push(
      am5xy.DateAxis.new(this.root, {
        maxDeviation: 0.1,
        baseInterval: { timeUnit: 'day', count: 1 },
        renderer: am5xy.AxisRendererX.new(this.root, {}),
      })
    );
    xAxis.get('renderer').labels.template.set('forceHidden', true);
    xAxis.get('renderer').grid.template.set('forceHidden', true);
  
    const yAxis = chart.yAxes.push(
      am5xy.ValueAxis.new(this.root, {
        renderer: am5xy.AxisRendererY.new(this.root, {}),
      })
    );
    yAxis.get('renderer').labels.template.set('forceHidden', true);
    yAxis.get('renderer').grid.template.set('forceHidden', true);
  
    const series = chart.series.push(
      am5xy.LineSeries.new(this.root, {
        name: 'Tasks',
        xAxis: xAxis,
        yAxis: yAxis,
        valueYField: 'count',
        valueXField: 'date',
        tooltip: am5.Tooltip.new(this.root, {
          labelText: '',
        }),
      })
    );
  
    series.get('tooltip')?.label.adapters.add('text', (text, target) => {
      const dataItem = target.dataItem;
      if (dataItem && dataItem.dataContext) {
        const context = dataItem.dataContext as { date: number; count: number }; // Raw data type
        const date = new Date(context.date); // Convert timestamp to Date
        const formattedDate = date.toLocaleDateString(); // Browser default date format
        const valueY = context.count; // Task count
        return `Date: ${formattedDate}\nTasks: ${valueY}`;
      }
      return text;
    });
  
    series.strokes.template.setAll({
      strokeWidth: 2,
    });
  
    series.data.setAll(
      this.last14DaysData.map((d) => ({
        date: new Date(d.date).getTime(),
        count: d.count,
      }))
    );
  
    chart.appear(1000, 100);
  }
  
  
  
  
  

  ngOnDestroy(): void {
    if (this.root) {
      this.root.dispose();
    }
  }
}
